"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SelfOrderThemeActivationReport = exports.SelfOrderThemeRejectionReason = exports.SelfOrderThemeManifest = exports.SelfOrderThemeAsset = exports.SelfOrderThemeAssetRole = exports.SelfOrderShape = exports.SelfOrderTypography = exports.SELF_ORDER_ALLOWED_FONT_FAMILIES = exports.SelfOrderSemanticColors = exports.SelfOrderColorToken = exports.ProductBrandMode = exports.SelfOrderThemeVersion = exports.SelfOrderThemeId = exports.ORLEN_STOP_CAFE_THEME_ID = exports.REFORM_LITE_DEFAULT_THEME_ID = exports.SELF_ORDER_THEME_SCHEMA_VERSION = void 0;
const zod_1 = require("zod");
/**
 * The signed self-order presentation artifact
 * (self-order spec v2.3 §4.3, §11.6–§11.10, §21.2–§21.3, §21.6–§21.7).
 *
 * **This artifact is data, never code.** The terminal must never load Dart,
 * JavaScript, native code or WASM from a theme. Every object here is `.strict()`
 * for that reason: an unknown key is a hard parse failure, not a field quietly
 * carried into the renderer. The spec's instruction — "do not smuggle new fields
 * into an unversioned JSON map" — is only enforceable if the schema refuses what
 * it does not recognise, so please do not relax it to `.passthrough()`.
 *
 * The signing envelope (size, SHA-256, Ed25519 signature, signing key, approval)
 * stays where it already lives, on `ProvisionArtifact`. What this schema
 * describes is the artifact's *contents*, which that envelope carries.
 */
/* -------------------------------------------------------------------------- */
/* Identity                                                                   */
/* -------------------------------------------------------------------------- */
/** Current manifest schema version. Bump additively; never reuse a number. */
exports.SELF_ORDER_THEME_SCHEMA_VERSION = 1;
/** The platform baseline and safe fallback (spec decision 11). */
exports.REFORM_LITE_DEFAULT_THEME_ID = 'reform-lite-default';
/** The first customer composition (spec decision 12). */
exports.ORLEN_STOP_CAFE_THEME_ID = 'orlen-stop-cafe';
exports.SelfOrderThemeId = zod_1.z
    .string()
    .regex(/^[a-z][a-z0-9-]{1,63}$/, 'lowercase kebab-case slug');
/** Semantic version of the theme content, independent of the artifact id. */
exports.SelfOrderThemeVersion = zod_1.z
    .string()
    .regex(/^\d+\.\d+\.\d+$/, 'semantic version, e.g. 3.0.0');
/**
 * How the Reform Lite product identity appears alongside customer branding.
 *
 * Customer branding must not remove the Reform Lite identity unless a later
 * commercial decision changes the rule (spec decision 14), so there is no
 * "customer only" member here. Adding one is a commercial decision, not a
 * schema change.
 */
exports.ProductBrandMode = zod_1.z.enum(['reform_lite_primary', 'powered_by_reform_lite']);
/* -------------------------------------------------------------------------- */
/* Colour                                                                     */
/* -------------------------------------------------------------------------- */
/**
 * `#rrggbb`, `#rrggbbaa`, or `rgba(r, g, b, a)`.
 *
 * Constrained rather than free text because this string is handed to a colour
 * parser on two platforms. A CSS value like `var(--x)` or `url(...)` must never
 * reach the web projection, and Flutter would reject it anyway — differently,
 * and later, and on the customer's screen.
 */
exports.SelfOrderColorToken = zod_1.z
    .string()
    .regex(/^(#[0-9a-fA-F]{6}([0-9a-fA-F]{2})?|rgba\(\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*\d{1,3}\s*,\s*(0|1|0?\.\d+)\s*\))$/, 'hex #rrggbb / #rrggbbaa, or rgba()');
/**
 * The semantic colour vocabulary, shared verbatim by the Flutter theme and the
 * web CSS custom properties (§21.2, §21.3).
 *
 * Semantic names, not brand names: `primary`, never `orlenRed`. A theme that
 * names its own brand in a token forces every consumer to know whose theme is
 * active, which is exactly what the theme layer exists to prevent.
 */
exports.SelfOrderSemanticColors = zod_1.z
    .object({
    primary: exports.SelfOrderColorToken,
    primaryPressed: exports.SelfOrderColorToken,
    canvas: exports.SelfOrderColorToken,
    contentSurface: exports.SelfOrderColorToken,
    navigationSurface: exports.SelfOrderColorToken,
    paper: exports.SelfOrderColorToken,
    textPrimary: exports.SelfOrderColorToken,
    textSecondary: exports.SelfOrderColorToken,
    textMuted: exports.SelfOrderColorToken,
    divider: exports.SelfOrderColorToken,
    brandDivider: exports.SelfOrderColorToken,
    softAttention: exports.SelfOrderColorToken,
    success: exports.SelfOrderColorToken,
    successSurface: exports.SelfOrderColorToken,
    preparing: exports.SelfOrderColorToken,
    focus: exports.SelfOrderColorToken,
    scrim: exports.SelfOrderColorToken,
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Typography and shape                                                       */
/* -------------------------------------------------------------------------- */
/**
 * Font families a theme may name.
 *
 * An allowlist, not free text (§4.3: "typography choices from an allowlist").
 * A licensed corporate font is added here only once its licence is recorded —
 * naming a family the terminal does not carry produces a silent fallback that
 * nobody notices until the customer does.
 */
exports.SELF_ORDER_ALLOWED_FONT_FAMILIES = [
    'Inter',
    'Roboto',
    'Noto Sans',
    'Source Sans 3',
];
exports.SelfOrderTypography = zod_1.z
    .object({
    fontFamily: zod_1.z.enum(exports.SELF_ORDER_ALLOWED_FONT_FAMILIES),
    /** Applied to every step; bounded so a theme cannot make text unreadable. */
    scaleFactor: zod_1.z.number().min(0.9).max(1.15).default(1),
    headingWeight: zod_1.z.number().int().min(500).max(900).default(700),
    bodyWeight: zod_1.z.number().int().min(300).max(600).default(400),
})
    .strict();
/**
 * Shape and elevation, within validated limits (§4.3, §11.9).
 *
 * Note what is *absent*: touch-target sizes. A theme may not reduce them
 * (Amendment 1, D1 — 48×48 minimum, 56×56 for the kiosk add action), so they are
 * not expressible here at all. A value a theme cannot set is a value a theme
 * cannot get wrong.
 */
exports.SelfOrderShape = zod_1.z
    .object({
    cardRadius: zod_1.z.number().int().min(0).max(24),
    controlRadius: zod_1.z.number().int().min(0).max(24),
    dialogRadius: zod_1.z.number().int().min(0).max(28),
    badgeRadius: zod_1.z.number().int().min(0).max(16),
    /** 0 = flat. Bounded because elevation is contrast on a glossy kiosk panel. */
    cardElevation: zod_1.z.number().min(0).max(8).default(0),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* Assets                                                                     */
/* -------------------------------------------------------------------------- */
exports.SelfOrderThemeAssetRole = zod_1.z.enum([
    'customer_logo_primary',
    'customer_logo_monochrome',
    'product_logo',
    'category_icon_sheet',
]);
/**
 * A referenced asset, identified by hash.
 *
 * Activation waits until every required asset verifies (§21.6.7). The hash is
 * the contract: the customer-supplied logo may never be recoloured, redrawn,
 * cropped, stretched or recombined (spec decision 13), and a hash mismatch is
 * how that is detected rather than trusted.
 */
exports.SelfOrderThemeAsset = zod_1.z
    .object({
    assetId: zod_1.z.string().min(1).max(128),
    role: exports.SelfOrderThemeAssetRole,
    sha256: zod_1.z.string().regex(/^[a-f0-9]{64}$/),
    contentType: zod_1.z.enum(['image/png', 'image/webp', 'image/svg+xml']),
    width: zod_1.z.number().int().min(1),
    height: zod_1.z.number().int().min(1),
    byteSize: zod_1.z.number().int().min(1),
    /** Alt text key, resolved from the i18n catalogue. Never literal copy. */
    semanticLabelKey: zod_1.z.string().min(1).max(128),
})
    .strict();
/* -------------------------------------------------------------------------- */
/* The manifest                                                               */
/* -------------------------------------------------------------------------- */
exports.SelfOrderThemeManifest = zod_1.z
    .object({
    schemaVersion: zod_1.z.literal(exports.SELF_ORDER_THEME_SCHEMA_VERSION),
    /** UI-port contract this theme was authored against (ADR-0007). */
    uiPortVersion: zod_1.z.string().regex(/^\d+\.\d+$/),
    themeId: exports.SelfOrderThemeId,
    themeVersion: exports.SelfOrderThemeVersion,
    displayName: zod_1.z.string().min(1).max(120),
    productBrandMode: exports.ProductBrandMode,
    supportsDarkMode: zod_1.z.boolean().default(false),
    colors: exports.SelfOrderSemanticColors,
    typography: exports.SelfOrderTypography,
    shape: exports.SelfOrderShape,
    assets: zod_1.z.array(exports.SelfOrderThemeAsset).max(16).default([]),
    /**
     * Branding copy keys only, in a customer namespace with English fallback.
     * Keys, never literal strings: a theme must not silently replace the meaning
     * of a functional control (§21.5).
     */
    presentationTextKeys: zod_1.z
        .record(zod_1.z.string().min(1).max(128), zod_1.z.string().min(1).max(128))
        .default({}),
})
    .strict()
    .superRefine((manifest, ctx) => {
    if (manifest.themeId === exports.REFORM_LITE_DEFAULT_THEME_ID) {
        if (manifest.productBrandMode !== 'reform_lite_primary') {
            ctx.addIssue({
                code: zod_1.z.ZodIssueCode.custom,
                path: ['productBrandMode'],
                message: 'the default theme must present Reform Lite as the primary identity',
            });
        }
        return;
    }
    // A customer theme has to carry its own primary logo — falling back to the
    // Reform Lite mark under someone else's brand is worse than failing.
    const hasCustomerLogo = manifest.assets.some((a) => a.role === 'customer_logo_primary');
    if (!hasCustomerLogo) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['assets'],
            message: 'a customer theme must declare a customer_logo_primary asset',
        });
    }
    if (manifest.productBrandMode !== 'powered_by_reform_lite') {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['productBrandMode'],
            message: 'a customer theme must keep the Powered by Reform Lite lockup (decision 14)',
        });
    }
});
/* -------------------------------------------------------------------------- */
/* Activation                                                                 */
/* -------------------------------------------------------------------------- */
/**
 * Why a downloaded theme was refused.
 *
 * Enumerated because each one is a different operational story: a signature
 * failure is a security event, an asset mismatch is a pipeline bug, and a
 * UI-port incompatibility is a release-ordering mistake. Collapsing them into
 * "activation failed" loses the only useful part of the alert.
 */
exports.SelfOrderThemeRejectionReason = zod_1.z.enum([
    'schema_version_unsupported',
    'ui_port_incompatible',
    'signature_invalid',
    'checksum_mismatch',
    'asset_missing',
    'asset_checksum_mismatch',
    'smoke_test_failed',
]);
/**
 * What the terminal reports after trying to activate a theme.
 *
 * `fellBackTo` is the honest part: on corruption the terminal keeps its
 * last-known-good customer theme, and only if none exists does it drop to
 * `reform-lite-default` — never a broken or blank logo (§4.3 rule 6).
 */
exports.SelfOrderThemeActivationReport = zod_1.z
    .object({
    terminalId: zod_1.z.string().min(1),
    requestedThemeId: exports.SelfOrderThemeId,
    requestedThemeVersion: exports.SelfOrderThemeVersion,
    outcome: zod_1.z.enum(['activated', 'rejected']),
    rejectionReason: exports.SelfOrderThemeRejectionReason.nullable().default(null),
    activeThemeId: exports.SelfOrderThemeId,
    activeThemeVersion: exports.SelfOrderThemeVersion,
    fellBackTo: zod_1.z.enum(['none', 'last_known_good', 'reform_lite_default']).default('none'),
    reportedAt: zod_1.z.string().datetime(),
})
    .strict()
    .superRefine((report, ctx) => {
    if (report.outcome === 'rejected' && report.rejectionReason === null) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['rejectionReason'],
            message: 'a rejected activation must say why',
        });
    }
    if (report.outcome === 'activated' && report.rejectionReason !== null) {
        ctx.addIssue({
            code: zod_1.z.ZodIssueCode.custom,
            path: ['rejectionReason'],
            message: 'an activated theme cannot carry a rejection reason',
        });
    }
});
//# sourceMappingURL=self-order-theme.js.map